import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import BarcodeScanner from '@/components/BarcodeScanner';
import { 
  Plus, 
  Trash2, 
  Save, 
  ArrowLeft,
  Search,
  ScanBarcode,
  Calculator,
  IndianRupee,
  Loader2,
  CheckCircle2,
  Package,
  X
} from 'lucide-react';

import { isCataloguePriced, cataloguePrice } from '@/lib/cataloguePricing';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

// Calls the Margin Master calculator and patches the matching line's unit_price
// when applied. Skips silently for unit != PCS / invalid inputs; warns via
// toast for no_match / non_positive cases (per spec). Honors the per-line
// `outward_manual_override` flag so a user-typed price is never overwritten.
// June 20, 2026 — Option B behaviour:
//   The product MASTER's `selling_price` (a.k.a `outward_selling_price`)
//   is the **source of truth** on every freshly-added invoice line. The
//   Margin Master engine is still called in the background — but ONLY to
//   compute a "suggested" price, NOT to overwrite the line's unit_price.
//   When the suggestion disagrees with the master by more than ~₹1 (or
//   any difference if the master is missing), we tag the line with
//   `slab_disagreement` so the JSX can render a small warning chip the
//   cashier can click to opt-in to the slab price.
async function applyMarginMasterToLine(setItems, products, itemIndex, productOrId) {
  var product = typeof productOrId === 'string'
    ? (products.find(function(p) { return p.id === productOrId; }) || null)
    : productOrId;
  if (!product) return;
  if (isCataloguePriced(product)) return;
  var unit = (product.unit || product.measurement_unit || '').toString().toUpperCase();
  if (unit !== 'PCS') return;
  // Category exclusion: any category whose name contains "oil" (Oils,
  // Edible Oils, Cooking Oil, Refined Oil, ...) skips margin-master
  // recompute entirely — the master's selling_price is used as-is.
  var category = (product.category || '').toString().toLowerCase();
  if (category.indexOf('oil') !== -1) return;
  var mrp = parseFloat(product.mrp);
  var inward = parseFloat(product.inward_price_incl_gst);
  if (!inward || inward <= 0) {
    // Legacy products that only stored excl-GST inward — reconstruct incl-GST
    // so Margin Master can still compute the suggestion.
    var excl = parseFloat(product.inward_price_excl_gst || product.inward_b2b_price || product.purchase_price);
    var gstPct = parseFloat(product.gst_percentage || 0);
    if (excl > 0) inward = +(excl * (1 + (gstPct / 100))).toFixed(2);
  }
  if (!mrp || !inward || mrp <= 0 || inward <= 0) return;
  try {
    var token = localStorage.getItem('token');
    var res = await axios.post(API + '/margin-master/calculate',
      { mrp: mrp, inward_price_incl_gst: inward, unit: 'PCS', category: product.category || null },
      { headers: { Authorization: 'Bearer ' + token } }
    );
    var d = res.data || {};
    if (!d.applied) return;
    var slabPrice = +d.outward_selling_price;
    setItems(function(prev) {
      return prev.map(function(it, i) {
        if (i !== itemIndex) return it;
        if (it.outward_manual_override) return it;
        // Master price stays the canonical line price; only attach the slab
        // suggestion metadata so the cell can render the warning chip.
        var masterPrice = parseFloat(it.unit_price || 0);
        var diff = Math.abs(slabPrice - masterPrice);
        // Warn the operator when the slab disagrees by ≥ ₹1 (any drift
        // smaller than that is rounding noise — surfacing it would create
        // chip-fatigue).
        var disagrees = !masterPrice || diff >= 1;
        return Object.assign({}, it, {
          slab_suggested_price: slabPrice,
          slab_our_margin_pct: d.our_margin_pct,
          slab_disagreement: disagrees,
        });
      });
    });
  } catch (err) {
    // Silently degrade — line keeps the master's price, no warning chip
    if (err && err.message) console.warn('margin-master calc failed', err.message);
  }
}

function InvoiceCreate() {
  var loadingState = React.useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var customersState = React.useState([]);
  var customers = customersState[0];
  var setCustomers = customersState[1];
  
  var productsState = React.useState([]);
  var products = productsState[0];
  var setProducts = productsState[1];
  
  var selectedCustomerState = React.useState(null);
  var selectedCustomer = selectedCustomerState[0];
  var setSelectedCustomer = selectedCustomerState[1];
  
  var invoiceDateState = React.useState(new Date().toISOString().split('T')[0]);
  var invoiceDate = invoiceDateState[0];
  var setInvoiceDate = invoiceDateState[1];
  
  var paymentModeState = React.useState('cash');
  var paymentMode = paymentModeState[0];
  var setPaymentMode = paymentModeState[1];
  
  var dueDateState = React.useState('');
  var dueDate = dueDateState[0];
  var setDueDate = dueDateState[1];
  
  var itemsState = React.useState([]);
  var items = itemsState[0];
  var setItems = itemsState[1];
  
  var isIGSTState = React.useState(false);
  var isIGST = isIGSTState[0];
  var setIsIGST = isIGSTState[1];
  
  var discountPercentState = React.useState(0);
  var discountPercent = discountPercentState[0];
  var setDiscountPercent = discountPercentState[1];
  
  var roundOffState = React.useState(0);
  var roundOff = roundOffState[0];
  var setRoundOff = roundOffState[1];
  
  var notesState = React.useState('');
  var notes = notesState[0];
  var setNotes = notesState[1];
  
  var scannerModeState = React.useState(false);
  var scannerMode = scannerModeState[0];
  var setScannerMode = scannerModeState[1];

  // Global scan input — used by the dedicated "Scan Barcode" box at the top
  // of the page so the operator can scan continuously without touching the
  // mouse. The newest scanned product always appears at row 0 (existing
  // per-row search inputs are unaffected — manual entry still works as before).
  var fastScanRef = React.useRef(null);
  var fastScanState = React.useState('');
  var fastScanValue = fastScanState[0];
  var setFastScanValue = fastScanState[1];
  var fastScanBusyState = React.useState(false);
  var fastScanBusy = fastScanBusyState[0];
  var setFastScanBusy = fastScanBusyState[1];

  // Auto-focus the Fast Scan input as soon as the page mounts so the
  // operator can scan immediately without clicking. preventScroll keeps
  // the viewport pinned at the top.
  React.useEffect(function() {
    var t = setTimeout(function() {
      if (fastScanRef.current) {
        try { fastScanRef.current.focus({ preventScroll: true }); }
        catch (_) { fastScanRef.current.focus(); }
      }
    }, 100);
    return function() { clearTimeout(t); };
  }, []);

  // Shift every numeric-keyed entry in a per-index state map by +1. Used when
  // we PREPEND a row so per-row state (search dropdowns, batches) stays
  // attached to the right row visually.
  function shiftMapPlusOne(m) {
    var out = {};
    Object.keys(m || {}).forEach(function(k) {
      var n = parseInt(k, 10);
      if (!isNaN(n)) out[String(n + 1)] = m[k];
      else out[k] = m[k];
    });
    return out;
  }

  // Move a row from `from` index to `to` index (used to bubble a duplicate
  // scan back to row 0). Per-index state maps are remapped in lockstep.
  function moveRowToTop(fromIdx) {
    if (fromIdx <= 0) return;
    setItems(function(prev) {
      var next = prev.slice();
      var row = next.splice(fromIdx, 1)[0];
      next.unshift(row);
      return next;
    });
    function remapToTop(m) {
      var out = {};
      Object.keys(m || {}).forEach(function(k) {
        var n = parseInt(k, 10);
        if (isNaN(n)) { out[k] = m[k]; return; }
        if (n === fromIdx) out['0'] = m[k];
        else if (n < fromIdx) out[String(n + 1)] = m[k];
        else out[String(n)] = m[k];
      });
      return out;
    }
    setSearchQueries(remapToTop);
    setSearchResults(remapToTop);
    setShowDropdowns(remapToTop);
    setSearchLoading(remapToTop);
    setItemBatches(remapToTop);
  }
  
  var submittingState = React.useState(false);
  var submitting = submittingState[0];
  var setSubmitting = submittingState[1];

  // Phase-2: Multi-MRP batch pickers keyed by item index.
  var itemBatchesState = React.useState({}); // { [idx]: [batch, batch, ...] }
  var itemBatches = itemBatchesState[0];
  var setItemBatches = itemBatchesState[1];

  // Product Search Mode States
  var searchModeState = React.useState('search'); // 'search' or 'dropdown'
  var searchMode = searchModeState[0];
  var setSearchMode = searchModeState[1];
  
  var searchQueryState = React.useState({});  // { [itemIndex]: query }
  var searchQueries = searchQueryState[0];
  var setSearchQueries = searchQueryState[1];
  
  var searchResultsState = React.useState({});  // { [itemIndex]: results[] }
  var searchResults = searchResultsState[0];
  var setSearchResults = searchResultsState[1];
  
  var searchLoadingState = React.useState({});  // { [itemIndex]: boolean }
  var searchLoading = searchLoadingState[0];
  var setSearchLoading = searchLoadingState[1];
  
  var showDropdownState = React.useState({});  // { [itemIndex]: boolean }
  var showDropdowns = showDropdownState[0];
  var setShowDropdowns = showDropdownState[1];

  // Refs for click outside detection
  var searchRefs = React.useRef({});

  // Edit mode detection via URL query ?editId=<id>
  var urlParams = new URLSearchParams(window.location.search);
  var editId = urlParams.get('editId');
  var isEditMode = !!editId;
  
  var editInvoiceNumberState = React.useState('');
  var editInvoiceNumber = editInvoiceNumberState[0];
  var setEditInvoiceNumber = editInvoiceNumberState[1];
  
  React.useEffect(function() {
    fetchCustomers();
    fetchProducts();
    if (isEditMode) {
      fetchInvoiceForEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  function fetchInvoiceForEdit() {
    var token = localStorage.getItem('token');
    // Fetch the invoice AND the current products list in parallel so we can
    // look up real-time stock per product (the invoice doesn't store stock,
    // and hardcoding 0 showed "Stock: 0" to users editing existing invoices).
    Promise.all([
      axios.get(API + '/invoices/' + editId, { headers: { Authorization: 'Bearer ' + token } }),
      axios.get(API + '/products', { headers: { Authorization: 'Bearer ' + token } })
    ]).then(function(res) {
      var inv = res[0].data;
      var allProducts = res[1].data || [];
      if (inv.status === 'paid') {
        toast.error('Paid invoices cannot be edited');
        window.location.href = '/invoices';
        return;
      }
      setEditInvoiceNumber(inv.invoice_number || '');
      setInvoiceDate(inv.invoice_date || new Date().toISOString().split('T')[0]);
      setDueDate(inv.due_date || '');
      setPaymentMode(inv.payment_mode || 'cash');
      setIsIGST(!!inv.is_igst);
      setDiscountPercent(inv.discount_percent || 0);
      setNotes(inv.notes || '');
      setRoundOff(inv.round_off || 0);
      setSelectedCustomer({
        id: inv.customer_id,
        name: inv.customer_name,
        gstin: inv.customer_gstin,
        address: inv.customer_address,
        phone: inv.customer_phone,
        email: inv.customer_email
      });
      
      // Build a product-id -> current_stock map for O(1) lookup
      var stockMap = {};
      allProducts.forEach(function(p) { stockMap[p.id] = Number(p.current_stock) || 0; });
      
      // Finalized/partial_paid invoices already deducted stock; when editing,
      // the user's "available to allocate" = current_stock + this invoice's old qty
      // (since reversal happens on submit). Draft invoices have no deduction yet.
      var stockDeducted = inv.status === 'finalized' || inv.status === 'partial_paid';
      var prodMap = {};
      allProducts.forEach(function(p) { prodMap[p.id] = p; });
      var editItems = (inv.items || []).map(function(it) {
        var currentStock = stockMap[it.product_id] || 0;
        var catPrice = it.price_manual_override ? null : cataloguePrice(prodMap[it.product_id]);
        var effective = stockDeducted ? (currentStock + (Number(it.quantity) || 0)) : currentStock;
        return {
          product_id: it.product_id,
          product_name: it.product_name,
          hsn_code: it.hsn_code || '',
          quantity: it.quantity || 0,
          unit: it.unit || 'PCS',
          mrp: it.mrp || 0,
          unit_price: catPrice || it.unit_price || 0,
          outward_manual_override: !!it.price_manual_override,
          batch_id: it.batch_id || null,
          batch_number: it.batch_number || null,
          discount_percent: it.discount_percent || 0,
          gst_percentage: it.gst_percentage || 0,
          available_stock: effective
        };
      });
      setItems(editItems);
    }).catch(function() {
      toast.error('Failed to load invoice for edit');
      window.location.href = '/invoices';
    });
  }

  function fetchCustomers() {
    var token = localStorage.getItem('token');
    // Try onboarding first (this is where customers actually live); fall back to legacy /customers silently
    axios.get(API + '/onboarding', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setCustomers(response.data || []);
    }).catch(function() {
      axios.get(API + '/customers', {
        headers: { Authorization: 'Bearer ' + token }
      }).then(function(r) { setCustomers(r.data || []); })
        .catch(function() { setCustomers([]); });
    });
  }

  function fetchProducts() {
    var token = localStorage.getItem('token');
    axios.get(API + '/products', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setProducts(response.data || []);
    }).catch(function(error) {
      toast.error('Failed to load products');
    });
  }

  // Product Search Function
  function searchProducts(query, itemIndex) {
    // Update query state
    var newQueries = Object.assign({}, searchQueries);
    newQueries[itemIndex] = query;
    setSearchQueries(newQueries);
    
    // Clear results if query too short
    if (query.length < 3) {
      var newResults = Object.assign({}, searchResults);
      newResults[itemIndex] = [];
      setSearchResults(newResults);
      return;
    }
    
    // Set loading
    var newLoading = Object.assign({}, searchLoading);
    newLoading[itemIndex] = true;
    setSearchLoading(newLoading);
    
    // Show dropdown
    var newShow = Object.assign({}, showDropdowns);
    newShow[itemIndex] = true;
    setShowDropdowns(newShow);
    
    var token = localStorage.getItem('token');
    axios.get(API + '/products/invoice-search', {
      params: { q: query, limit: 10 },
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      var data = response.data || {};
      var results = data.results || [];
      var newResults = Object.assign({}, searchResults);
      newResults[itemIndex] = results;
      setSearchResults(newResults);

      var newLoading = Object.assign({}, searchLoading);
      newLoading[itemIndex] = false;
      setSearchLoading(newLoading);

      // Barcode-mode UX: scanner inputs (all-digit, >=8 chars) must produce
      // either 1 exact hit (auto-select) or 0 hits (error toast). Anything
      // in-between means duplicate-barcode and the operator must
      // disambiguate manually before invoicing.
      if (data.mode === 'barcode') {
        if (results.length === 0) {
          toast.error(data.message || 'Barcode not found in Master Catalogue');
        } else if (data.duplicate_barcode) {
          toast.error('Duplicate barcode detected in Master Catalogue. Pick the correct SKU manually.');
        } else if (results.length === 1) {
          // Single exact hit → auto-select and close the dropdown.
          selectSearchProduct(itemIndex, results[0]);
          var clearShow = Object.assign({}, showDropdowns);
          clearShow[itemIndex] = false;
          setShowDropdowns(clearShow);
          var clearQ = Object.assign({}, searchQueries);
          clearQ[itemIndex] = '';
          setSearchQueries(clearQ);
          var clearR = Object.assign({}, searchResults);
          clearR[itemIndex] = [];
          setSearchResults(clearR);
        }
      }
    }).catch(function() {
      var newLoading = Object.assign({}, searchLoading);
      newLoading[itemIndex] = false;
      setSearchLoading(newLoading);
    });
  }

  // Select product from search results
  function selectSearchProduct(itemIndex, product) {
    // Update item with product data
    var newItems = items.map(function(item, i) {
      if (i !== itemIndex) return item;
      return Object.assign({}, item, {
        product_id: product.id,
        product_name: product.name,
        hsn_code: product.hsn_code || '',
        unit: product.unit || 'PCS',
        is_loose: !!product.is_loose,
        mrp: product.mrp || 0,
        // Prefer the canonical `outward_selling_price` from the product master.
        // Fallback to legacy `selling_price`, then MRP only as a last resort —
        // earlier the chain skipped `outward_selling_price`, which was set on
        // newer products, so the field defaulted to MRP for those rows.
        unit_price: product.outward_selling_price || product.selling_price || product.mrp || 0,
        gst_percentage: product.gst_percentage || 0,
        available_stock: product.current_stock || 0,
        batch_id: null,
        batch_number: null,
        // Reset auto-pricing flags on fresh product selection so the engine
        // can override unit_price below if Unit=PCS and a Margin Master row matches.
        outward_manual_override: false,
        outward_auto_applied: false,
      });
    });
    setItems(newItems);
    fetchBatchesForItem(itemIndex, product.id);
    // Auto-fill via Margin Master (debounced/silent on misses)
    applyMarginMasterToLine(setItems, products, itemIndex, product);
    
    // Clear search state for this item
    var newQueries = Object.assign({}, searchQueries);
    newQueries[itemIndex] = '';
    setSearchQueries(newQueries);
    
    var newShow = Object.assign({}, showDropdowns);
    newShow[itemIndex] = false;
    setShowDropdowns(newShow);
    
    var newResults = Object.assign({}, searchResults);
    newResults[itemIndex] = [];
    setSearchResults(newResults);
    
    // Focus on quantity input
    setTimeout(function() {
      var qtyInput = document.querySelector('[data-testid="qty-input-' + itemIndex + '"]');
      if (qtyInput) qtyInput.focus();
    }, 100);
  }

  // Phase-2: fetch active batches for a product; if ≥1, auto-pick the first one.
  // Phase-3: For loose products (KG/GRAMS), DON'T auto-pick a batch — instead
  // aggregate every batch's `current_stock` into the line's `available_stock`
  // and leave `batch_id=null`. The backend FIFO-spills the qty across batches
  // at finalize time. UI hides the batch picker for these lines.
  function fetchBatchesForItem(itemIndex, productId) {
    if (!productId) return;
    var token = localStorage.getItem('token');
    axios.get(API + '/inventory/batches?only_available=true&product_id=' + encodeURIComponent(productId), {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      var batches = res.data || [];
      // Functional update — avoids stale-closure overwrites when several
      // lines fetch batches at the same time (picker would vanish on rows).
      setItemBatches(function(prev) {
        var nextBatches = Object.assign({}, prev);
        nextBatches[itemIndex] = batches;
        return nextBatches;
      });
      if (batches.length === 0) return;

      // Decide loose vs packed using the line's `unit` (set from product master
      // earlier). If it's KG / GRAMS / etc — aggregate; otherwise auto-pick.
      var lineUnit = ((items[itemIndex] && items[itemIndex].unit) || 'PCS').toString().toUpperCase();
      var LOOSE = ['KG','KGS','KILO','KILOGRAM','KILOGRAMS','GRAM','GRAMS','GM','GMS','G'];
      if (LOOSE.indexOf(lineUnit) >= 0) {
        var sum = 0;
        for (var i = 0; i < batches.length; i++) {
          sum += parseFloat(batches[i].current_stock) || 0;
        }
        // Use the latest-MRP batch's MRP as the line MRP (loose batches share MRP per spec)
        var sortedByDate = batches.slice().sort(function(a, b) {
          var ad = a.last_inward_date || a.first_inward_date || '';
          var bd = b.last_inward_date || b.first_inward_date || '';
          return ad < bd ? 1 : ad > bd ? -1 : 0;
        });
        var latestMrp = (sortedByDate[0] && sortedByDate[0].mrp) || (batches[0] && batches[0].mrp) || 0;
        setItems(function(prev) {
          return prev.map(function(it, i) {
            if (i !== itemIndex) return it;
            return Object.assign({}, it, {
              batch_id: null,
              batch_number: null,
              // Master catalogue MRP wins — batch MRP is frozen at GRN time
              // and goes STALE after price updates (PROD-019022 bug, Jul 2026).
              mrp: (parseFloat(it.mrp) > 0 ? it.mrp : latestMrp),
              available_stock: Math.round(sum * 1000) / 1000,
              is_loose: true,
            });
          });
        });
        return;
      }

      // PCS / packed: auto-pick first batch for stock/batch linkage, but keep
      // the MASTER catalogue pricing on the line (explicitPick=false). Batch
      // pricing applies only when the user explicitly selects a batch.
      applyBatchToItem(itemIndex, batches[0], false);
    }).catch(function() { /* Batch fetch failed — falls back to aggregate stock. */ });
  }

  function applyBatchToItem(itemIndex, batch, explicitPick) {
    if (!batch) return;
    if (explicitPick === undefined) explicitPick = true;
    // Functional setter so concurrent updates from `selectSearchProduct` aren't clobbered.
    setItems(function(prevItems) {
      return prevItems.map(function(item, i) {
        if (i !== itemIndex) return item;
        // Pick the best outward price available on the batch row itself first.
        // The backend now computes + stores `outward_selling_price` on every
        // batch via Margin Master. If the batch row carries a valid non-MRP
        // value, use it; else fall back to MRP and trigger a server-side
        // re-compute right after (handled below).
        var batchOutward = parseFloat(batch.outward_selling_price);
        var seedPrice = (!isNaN(batchOutward) && batchOutward > 0 && batchOutward !== Number(batch.mrp))
          ? batchOutward
          : (Number(batch.mrp) || item.unit_price);
        var catProd = (products || []).find(function(p) { return p.id === batch.product_id; });
        var catPrice = cataloguePrice(catProd);
        if (catPrice) {
          // KG: price always comes from the Master Catalogue, never the batch.
          return Object.assign({}, item, {
            batch_id: batch.id,
            batch_number: batch.batch_number,
            mrp: batch.mrp || item.mrp,
            unit_price: item.outward_manual_override ? item.unit_price : catPrice,
            available_stock: batch.current_stock,
            outward_auto_applied: false,
          });
        }
        if (!explicitPick) {
          // Auto-picked batch: batch-wise billing (Sep 2026) — the line takes
          // the BATCH's MRP and batch-derived selling price so billing always
          // reflects the physical batch being sold.
          return Object.assign({}, item, {
            batch_id: batch.id,
            batch_number: batch.batch_number,
            mrp: batch.mrp || item.mrp,
            unit_price: seedPrice,
            available_stock: batch.current_stock,
            outward_auto_applied: !!(batchOutward && batchOutward > 0),
          });
        }
        return Object.assign({}, item, {
          batch_id: batch.id,
          batch_number: batch.batch_number,
          mrp: batch.mrp,
          unit_price: seedPrice,
          available_stock: batch.current_stock,
          outward_auto_applied: !!(batchOutward && batchOutward > 0),
        });
      });
    });

    // Always trigger a fresh Margin Master compute using the BATCH's mrp + inward_incl_gst
    // — this fixes the case where `outward_selling_price` on the batch row is
    // null / 0 / equal to MRP (e.g. legacy batches whose inward_incl was 0 at
    // creation time). Skips silently for unit != PCS or insufficient inputs.
    (async function() {
      if (!explicitPick) return;  // master pricing already applied at selection time
      var prod = (products || []).find(function(p) { return p.id === batch.product_id; });
      if (isCataloguePriced(prod)) return;
      var unit = (batch.measurement_unit || '').toString().toUpperCase();
      if (!unit) {
        unit = ((prod && (prod.unit || prod.measurement_unit)) || 'PCS').toString().toUpperCase();
      }
      if (unit !== 'PCS') return;
      var mrp = parseFloat(batch.mrp);
      // Inward price priority: batch → product master fields. Legacy batches
      // were created when `inward_price_incl_gst` wasn't being persisted, so
      // we fall back to the product's canonical inward fields so margin
      // calculation still works (was previously bailing → price stuck at MRP).
      var inward = parseFloat(batch.inward_price_incl_gst);
      if (!inward || inward <= 0) {
        if (prod) {
          inward = parseFloat(prod.inward_price_incl_gst);
          if (!inward || inward <= 0) {
            // Reconstruct incl-GST from excl-GST + product GST%.
            var excl = parseFloat(prod.inward_price_excl_gst || prod.inward_b2b_price || prod.purchase_price);
            var gstPct = parseFloat(prod.gst_percentage || 0);
            if (excl > 0) inward = +(excl * (1 + (gstPct / 100))).toFixed(2);
          }
        }
      }
      if (!mrp || !inward || mrp <= 0 || inward <= 0) return;
      try {
        var token = localStorage.getItem('token');
        var res = await axios.post(API + '/margin-master/calculate',
          { mrp: mrp, inward_price_incl_gst: inward, unit: 'PCS' },
          { headers: { Authorization: 'Bearer ' + token } }
        );
        var d = res.data || {};
        if (d.applied && d.outward_selling_price > 0) {
          setItems(function(prev) {
            return prev.map(function(it, i) {
              if (i !== itemIndex) return it;
              if (it.outward_manual_override) return it;
              return Object.assign({}, it, {
                unit_price: d.outward_selling_price,
                outward_auto_applied: true,
              });
            });
          });
        }
      } catch (e) { /* silently degrade — line already has a fallback price */ }
    })();
  }

  function handleBatchPickerChange(itemIndex, batchId) {
    var batches = itemBatches[itemIndex] || [];
    var picked = batches.find(function(b) { return b.id === batchId; });
    if (picked) applyBatchToItem(itemIndex, picked);
  }

  // Insert a new line right below `idx` for the SAME product, auto-picking the
  // next batch not already used on the invoice — lets the user bill one
  // product across multiple batches without re-searching it.
  function addBatchLine(idx) {
    var src = items[idx];
    if (!src || !src.product_id) return;
    var rowBatches = itemBatches[idx] || [];
    var used = {};
    items.forEach(function(it) {
      if (it.product_id === src.product_id && it.batch_id) used[it.batch_id] = true;
    });
    var next = rowBatches.find(function(b) { return !used[b.id]; });
    if (!next) {
      toast.warning('All batches of this product are already on the invoice');
      return;
    }
    var insertAt = idx + 1;
    var newRow = Object.assign({}, src, {
      quantity: 1,
      batch_id: null,
      batch_number: null,
      outward_manual_override: false,
      outward_auto_applied: false,
      slab_disagreement: false,
      slab_suggested_price: null,
      slab_our_margin_pct: null,
    });
    setItems(function(prev) {
      var out = prev.slice();
      out.splice(insertAt, 0, newRow);
      return out;
    });
    // Shift per-index state maps for rows at/after the insert point (+1),
    // then attach the same batch list to the new row.
    function shiftFrom(m) {
      var out = {};
      Object.keys(m || {}).forEach(function(k) {
        var n = parseInt(k, 10);
        if (isNaN(n)) { out[k] = m[k]; return; }
        out[n >= insertAt ? String(n + 1) : k] = m[k];
      });
      return out;
    }
    setSearchQueries(shiftFrom);
    setSearchResults(shiftFrom);
    setShowDropdowns(shiftFrom);
    setSearchLoading(shiftFrom);
    setItemBatches(function(prev) {
      var out = shiftFrom(prev);
      out[String(insertAt)] = rowBatches;
      return out;
    });
    // Apply the auto-picked batch (batch MRP + batch selling price + margin recompute).
    applyBatchToItem(insertAt, next, true);
    toast.success('Added line for ' + next.batch_number);
  }

  // Shared batch picker UI — used by both Search and Dropdown product modes.
  function renderBatchPicker(item, idx) {
    if (!item.product_id) return null;
    var rowBatches = itemBatches[idx] || [];
    if (rowBatches.length === 0) return null;
    var usedElsewhere = {};
    items.forEach(function(it, i) {
      if (i !== idx && it.product_id === item.product_id && it.batch_id) usedElsewhere[it.batch_id] = true;
    });
    var hasUnusedBatch = rowBatches.some(function(b) {
      return !usedElsewhere[b.id] && b.id !== item.batch_id;
    });
    if (item.is_loose) {
      return React.createElement('div', {
        className: 'px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2',
        'data-testid': 'loose-aggregate-badge-' + idx,
      },
        React.createElement('span', { className: 'text-xs font-semibold text-emerald-800 whitespace-nowrap' }, 'Loose stock · auto-FIFO'),
        React.createElement('span', { className: 'text-xs text-emerald-700' },
          'Total available: ' + (item.available_stock || 0) + ' ' + (item.unit || 'KG') + ' across ' + rowBatches.length + ' batch(es)'
        )
      );
    }
    return React.createElement('div', { className: 'px-2 py-1.5 bg-amber-50 border border-amber-200 rounded-lg', 'data-testid': 'batch-picker-wrap-' + idx },
      React.createElement('div', { className: 'flex items-center gap-2' },
        React.createElement('span', { className: 'text-xs font-semibold text-amber-800 whitespace-nowrap' }, 'Batch:'),
        React.createElement('select', {
          value: item.batch_id || '',
          onChange: function(e) { handleBatchPickerChange(idx, e.target.value); },
          className: 'flex-1 h-7 px-2 text-xs border border-amber-300 rounded bg-white',
          'data-testid': 'batch-picker-' + idx
        },
          rowBatches.map(function(b) {
            var inUse = usedElsewhere[b.id];
            return React.createElement('option', { key: b.id, value: b.id, disabled: !!inUse },
              '\u20B9' + (b.mrp || 0).toFixed(2) + ' \u00B7 ' + b.batch_number + ' \u00B7 Available ' + b.current_stock + ' ' + (item.unit || 'PCS') + (inUse ? ' \u00B7 in use' : ''));
          })
        ),
        hasUnusedBatch && React.createElement('button', {
          type: 'button',
          onClick: function() { addBatchLine(idx); },
          className: 'h-7 px-2 text-xs font-semibold bg-amber-600 text-white rounded hover:bg-amber-700 whitespace-nowrap flex items-center gap-1',
          title: 'Add another line for a different batch of this product',
          'data-testid': 'add-batch-line-' + idx
        },
          React.createElement(Plus, { className: 'w-3 h-3' }),
          'Add Batch'
        )
      )
    );
  }

  // Close dropdown when clicking outside
  function closeDropdown(itemIndex) {
    var newShow = Object.assign({}, showDropdowns);
    newShow[itemIndex] = false;
    setShowDropdowns(newShow);
  }

  // Clear product selection
  function clearProductSelection(itemIndex) {
    var newItems = items.map(function(item, i) {
      if (i !== itemIndex) return item;
      return Object.assign({}, item, {
        product_id: '',
        product_name: '',
        hsn_code: '',
        unit: 'PCS',
        unit_price: 0,
        gst_percentage: 0,
        available_stock: 0
      });
    });
    setItems(newItems);
    
    var newQueries = Object.assign({}, searchQueries);
    newQueries[itemIndex] = '';
    setSearchQueries(newQueries);
  }

  function selectCustomer(customerId) {
    var customer = customers.find(function(c) { return c.id === customerId; });
    setSelectedCustomer(customer);
  }

  // Customer type-to-search picker state (Oct 2026 — replaces plain dropdown)
  var custQueryState = React.useState('');
  var custQuery = custQueryState[0];
  var setCustQuery = custQueryState[1];
  var custOpenState = React.useState(false);
  var custOpen = custOpenState[0];
  var setCustOpen = custOpenState[1];

  function renderCustomerPicker() {
    if (selectedCustomer) {
      return React.createElement('div', {
        className: 'flex items-center justify-between gap-2 h-10 px-3 border border-blue-200 bg-blue-50 rounded-md',
        'data-testid': 'customer-selected-chip'
      },
        React.createElement('span', { className: 'text-sm font-medium text-slate-800 truncate' },
          selectedCustomer.name + (selectedCustomer.gstin ? ' (' + selectedCustomer.gstin + ')' : '')),
        React.createElement('button', {
          type: 'button',
          onClick: function() { setSelectedCustomer(null); setCustQuery(''); setCustOpen(false); },
          className: 'text-slate-400 hover:text-red-500 shrink-0',
          title: 'Change customer',
          'data-testid': 'customer-clear-btn'
        }, React.createElement(X, { className: 'w-4 h-4' }))
      );
    }
    var q = custQuery.trim().toLowerCase();
    var matches = (q
      ? customers.filter(function(c) {
          return (c.name || '').toLowerCase().indexOf(q) >= 0 || (c.gstin || '').toLowerCase().indexOf(q) >= 0;
        })
      : customers.slice()
    ).sort(function(a, b) {
      return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    }).slice(0, 50);
    return React.createElement('div', { className: 'relative' },
      React.createElement(Search, { className: 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none' }),
      React.createElement('input', {
        value: custQuery,
        onChange: function(e) { setCustQuery(e.target.value); setCustOpen(true); },
        onFocus: function() { setCustOpen(true); },
        onBlur: function() { setTimeout(function() { setCustOpen(false); }, 150); },
        placeholder: 'Search customer by name…',
        className: 'w-full h-10 pl-9 pr-3 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
        'data-testid': 'customer-search-input'
      }),
      custOpen && React.createElement('div', {
        className: 'absolute z-40 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg',
        'data-testid': 'customer-suggestions'
      },
        matches.length === 0
          ? React.createElement('div', { className: 'px-3 py-2.5 text-sm text-slate-400 italic' }, 'No customers match "' + custQuery + '"')
          : matches.map(function(c) {
              return React.createElement('button', {
                key: c.id, type: 'button',
                onMouseDown: function(e) { e.preventDefault(); selectCustomer(c.id); setCustOpen(false); setCustQuery(''); },
                className: 'w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-50 last:border-0',
                'data-testid': 'customer-option-' + c.id
              },
                React.createElement('span', { className: 'font-medium text-slate-800' }, c.name),
                c.gstin ? React.createElement('span', { className: 'text-xs text-slate-500 ml-2' }, c.gstin) : null
              );
            })
      )
    );
  }

  function addItem() {
    setItems(items.concat([{
      product_id: '',
      product_name: '',
      hsn_code: '',
      quantity: 1,
      unit: 'PCS',
      unit_price: 0,
      discount_percent: 0,
      gst_percentage: 0,
      available_stock: 0
    }]));
  }

  function removeItem(index) {
    setItems(items.filter(function(_, i) { return i !== index; }));
  }

  function updateItem(index, field, value) {
    var newItems = items.map(function(item, i) {
      if (i !== index) return item;
      var updated = Object.assign({}, item);
      updated[field] = value;
      
      // Auto-pricing override semantics for invoice lines (Unit=PCS only).
      if (field === 'unit_price') {
        // User typed a price — lock auto-pricing for this line until a fresh
        // product selection clears it, and clear the slab-disagreement chip
        // since the cashier has intentionally taken action on the price.
        updated.outward_manual_override = true;
        updated.outward_auto_applied = false;
        updated.slab_disagreement = false;
      }

      // If product selected, populate fields
      if (field === 'product_id' && value) {
        var product = products.find(function(p) { return p.id === value; });
        if (product) {
          updated.product_name = product.name;
          updated.hsn_code = product.hsn_code || '';
          updated.unit = product.measurement_unit || product.unit || 'PCS';
          updated.is_loose = !!product.is_loose;
          updated.mrp = product.mrp || 0;
          updated.unit_price = product.outward_selling_price || product.selling_price || product.mrp || 0;
          updated.gst_percentage = product.gst_percentage || 0;
          updated.available_stock = product.current_stock || 0;
          updated.batch_id = null;
          updated.batch_number = null;
          // Reset auto-pricing flags so the engine can fire fresh below
          updated.outward_manual_override = false;
          updated.outward_auto_applied = false;
          updated.slab_disagreement = false;
          updated.slab_suggested_price = null;
          updated.slab_our_margin_pct = null;
        }
        // Also load batches (async — the picker will auto-select first on arrival).
        fetchBatchesForItem(index, value);
      }
      
      return updated;
    });
    setItems(newItems);

    // Kick off Margin Master auto-fill once state has been queued to update.
    if (field === 'product_id' && value) {
      var product2 = products.find(function(p) { return p.id === value; });
      if (product2) applyMarginMasterToLine(setItems, products, index, product2);
    }
  }

  function handleBarcodeProductFound(product) {
    // Newest-on-top UX (Dec 2026 spec change): if this product is already
    // on the invoice (and not a different batch), bump quantity AND move
    // it to row 0. Otherwise PREPEND a fresh row at index 0 and shift all
    // per-index state (+1) so search dropdowns / batch pickers stay
    // attached to the right rows visually.
    var existingIndex = items.findIndex(function(item) { return item.product_id === product.id; });
    if (existingIndex >= 0) {
      var ex = items[existingIndex];
      var current = parseFloat(ex.quantity) || 0;
      var batchAvail = parseFloat(ex.available_stock);
      // Batch-wise billing: never bump a line past its batch's stock — start a
      // NEW line instead so the user allocates the extra qty to another batch.
      if (ex.batch_id && !isNaN(batchAvail) && current + 1 > batchAvail) {
        toast.warning(ex.batch_number + ' has only ' + batchAvail + ' left — added a new line, pick another batch');
      } else {
        updateItem(existingIndex, 'quantity', current + 1);
        moveRowToTop(existingIndex);
        toast.success('Qty +1: ' + product.name);
        return;
      }
    }
    // Brand-new line — prepend so it appears at the top of the table.
    var newRow = {
      product_id: product.id,
      product_name: product.name,
      hsn_code: product.hsn_code || '',
      quantity: 1,
      unit: product.measurement_unit || 'PCS',
      is_loose: !!product.is_loose,
      mrp: product.mrp || 0,
      unit_price: product.outward_selling_price || product.selling_price || product.mrp || 0,
      discount_percent: 0,
      gst_percentage: product.gst_percentage || 0,
      available_stock: product.current_stock || 0,
      batch_id: null,
      batch_number: null,
      outward_manual_override: false,
      outward_auto_applied: false,
    };
    setItems(function(prev) { return [newRow].concat(prev); });
    // Shift all per-index state so existing rows keep their dropdowns / batches.
    setSearchQueries(shiftMapPlusOne);
    setSearchResults(shiftMapPlusOne);
    setShowDropdowns(shiftMapPlusOne);
    setSearchLoading(shiftMapPlusOne);
    setItemBatches(shiftMapPlusOne);
    // The new row lives at index 0 — fetch batches + run Margin-Master for it.
    fetchBatchesForItem(0, product.id);
    applyMarginMasterToLine(setItems, products, 0, product);
    toast.success('Added: ' + product.name);
  }

  // Global "Scan Barcode" input handler — looks up the scanned code in the
  // Master Catalogue and feeds it through `handleBarcodeProductFound` so
  // the newest scan lands at row 0. The page never scrolls; the input is
  // cleared + re-focused so the operator can scan the next product
  // immediately without touching the mouse.
  function handleFastScanSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    var code = (fastScanValue || '').trim();
    if (!code) return;
    if (fastScanBusy) return;
    setFastScanBusy(true);
    var token = localStorage.getItem('token');
    axios.get(API + '/products/invoice-search', {
      params: { q: code, limit: 5 },
      headers: { Authorization: 'Bearer ' + token },
    }).then(function(res) {
      var data = res.data || {};
      var results = data.results || [];
      if (data.mode === 'barcode' && data.duplicate_barcode) {
        toast.error('Duplicate barcode in Master Catalogue — pick the correct SKU manually.');
      } else if (results.length === 1) {
        handleBarcodeProductFound(results[0]);
      } else if (results.length === 0) {
        toast.error('Barcode not found: ' + code);
      } else {
        toast.warning(results.length + ' matches — refine the scan or use per-row search.');
      }
    }).catch(function() {
      toast.error('Scan lookup failed');
    }).finally(function() {
      // Clear + refocus regardless of outcome so the scanner is always
      // ready for the next product. No page scroll — focus() with
      // {preventScroll:true} keeps the viewport pinned at the top.
      setFastScanValue('');
      setFastScanBusy(false);
      setTimeout(function() {
        if (fastScanRef.current) {
          try { fastScanRef.current.focus({ preventScroll: true }); }
          catch (_) { fastScanRef.current.focus(); }
        }
      }, 0);
    });
  }

  function calculateItemTotal(item) {
    // `unit_price` stores the selling price which is INCLUSIVE of GST
    // (matches how Product Master `selling_price` is maintained by the business).
    // Back-calculate taxable + GST so we never double-charge tax on the line.
    var gross = (item.quantity || 0) * (item.unit_price || 0);
    var discount = gross * (item.discount_percent || 0) / 100;
    var lineIncl = gross - discount; // final invoice line total (incl GST)
    var gstPct = item.gst_percentage || 0;
    var taxable = gstPct > 0 ? lineIncl / (1 + gstPct / 100) : lineIncl;
    var gst = lineIncl - taxable;
    return {
      taxable: taxable,
      gst: gst,
      total: lineIncl
    };
  }

  function calculateTotals() {
    var subtotal = 0;      // taxable (excl GST) — back-calculated from inclusive line totals
    var totalGST = 0;
    var itemsIncl = 0;     // sum of line totals inclusive of GST (before invoice-level discount)

    items.forEach(function(item) {
      var calc = calculateItemTotal(item);
      subtotal += calc.taxable;
      totalGST += calc.gst;
      itemsIncl += calc.total;
    });

    // Invoice-level discount is applied on the inclusive total so the customer
    // sees the exact rupee reduction they expect (VAT is proportionally reduced).
    var invoiceDiscount = itemsIncl * discountPercent / 100;
    var discountFactor = itemsIncl > 0 ? (itemsIncl - invoiceDiscount) / itemsIncl : 1;
    var finalTaxable = subtotal * discountFactor;
    var finalGST = totalGST * discountFactor;
    var grandTotal = finalTaxable + finalGST + (parseFloat(roundOff) || 0);

    return {
      subtotal: finalTaxable,
      invoiceDiscount: invoiceDiscount,
      subtotalAfterDiscount: finalTaxable,
      cgst: isIGST ? 0 : finalGST / 2,
      sgst: isIGST ? 0 : finalGST / 2,
      igst: isIGST ? finalGST : 0,
      totalGST: finalGST,
      grandTotal: grandTotal
    };
  }

  function createInvoice(finalize) {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    // Validate items
    for (var i = 0; i < items.length; i++) {
      if (!items[i].product_id) {
        toast.error('Please select a product for item #' + (i + 1));
        return;
      }
      if (items[i].quantity <= 0) {
        toast.error('Quantity must be greater than 0 for ' + items[i].product_name);
        return;
      }
      // TEMP: oversell allowed during stock correction — convert hard block to soft warning
      if (items[i].quantity > items[i].available_stock) {
        toast.warning('Low stock for ' + items[i].product_name + ' (Available: ' + items[i].available_stock + ') — proceeding anyway');
      }
    }
    
    setSubmitting(true);
    var token = localStorage.getItem('token');
    
    var invoiceItems = items.map(function(item) {
      var calc = calculateItemTotal(item);
      var qtyNum = parseFloat(item.quantity);
      if (isNaN(qtyNum)) qtyNum = 0;
      return {
        product_id: item.product_id,
        product_name: item.product_name,
        hsn_code: item.hsn_code,
        quantity: qtyNum,
        unit: item.unit,
        mrp: item.mrp || 0,
        unit_price: item.unit_price,
        discount_percent: item.discount_percent || 0,
        discount_amount: 0,
        gst_percentage: item.gst_percentage,
        taxable_amount: calc.taxable,
        cgst_amount: isIGST ? 0 : calc.gst / 2,
        sgst_amount: isIGST ? 0 : calc.gst / 2,
        igst_amount: isIGST ? calc.gst : 0,
        total_amount: calc.total,
        batch_id: item.batch_id || null,
        batch_number: item.batch_number || null,
        price_manual_override: !!item.outward_manual_override
      };
    });
    
    var payload = {
      customer_id: selectedCustomer.id,
      invoice_date: invoiceDate,
      payment_mode: paymentMode,
      due_date: dueDate || null,
      items: invoiceItems,
      discount_percent: discountPercent,
      discount_amount: 0,
      round_off: parseFloat(roundOff) || 0,
      notes: notes,
      is_igst: isIGST
    };
    
    var httpMethod = isEditMode ? axios.put : axios.post;
    var httpUrl = isEditMode ? (API + '/invoices/' + editId) : (API + '/invoices');
    
    httpMethod(httpUrl, payload, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      var invoiceId = response.data.id || editId;
      var invoiceNumber = response.data.invoice_number || editInvoiceNumber;
      
      if (finalize) {
        // Finalize after save (works for both create and edit paths).
        axios.post(API + '/invoices/' + invoiceId + '/finalize', {}, {
          headers: { Authorization: 'Bearer ' + token }
        }).then(function() {
          toast.success('Invoice ' + invoiceNumber + (isEditMode ? ' updated & finalized!' : ' created and finalized!'));
          window.location.href = '/invoices';
        }).catch(function(error) {
          toast.error('Invoice ' + (isEditMode ? 'updated' : 'created') + ' but finalization failed: ' + (error.response?.data?.detail || 'Unknown error'));
          setSubmitting(false);
        });
        return;
      }
      
      if (isEditMode) {
        toast.success('Invoice ' + invoiceNumber + ' updated');
      } else {
        toast.success('Invoice ' + invoiceNumber + ' saved as draft');
      }
      window.location.href = '/invoices';
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
      setSubmitting(false);
    });
  }

  function formatCurrency(val) {
    return '₹' + (val || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  var totals = calculateTotals();

  return React.createElement('div', { className: 'min-h-screen bg-gray-50', 'data-testid': 'invoice-create' },
    // Header
    React.createElement('div', { className: 'bg-white border-b shadow-sm sticky top-0 z-10' },
      React.createElement('div', { className: 'flex items-center justify-between px-6 py-3' },
        React.createElement('div', { className: 'flex items-center gap-4' },
          React.createElement(Button, { 
            variant: 'ghost', 
            size: 'sm',
            onClick: function() { window.location.href = '/invoices'; }
          },
            React.createElement(ArrowLeft, { className: 'w-4 h-4 mr-1' }),
            'Back'
          ),
          React.createElement('h1', { className: 'text-2xl font-bold text-slate-800' }, isEditMode ? ('Edit Invoice ' + editInvoiceNumber) : 'Create Invoice')
        ),
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(Button, { 
            variant: scannerMode ? 'default' : 'outline', 
            onClick: function() { setScannerMode(!scannerMode); }, 
            className: scannerMode ? 'bg-green-600 hover:bg-green-700' : ''
          },
            React.createElement(ScanBarcode, { className: 'w-4 h-4 mr-2' }),
            scannerMode ? 'Scanner ON' : 'Barcode Scanner'
          )
        )
      ),
      // Barcode scanner when active
      scannerMode && React.createElement('div', { className: 'px-6 pb-3' },
        React.createElement('div', { className: 'bg-green-50 border border-green-200 rounded-lg p-3' },
          React.createElement(BarcodeScanner, {
            onProductFound: handleBarcodeProductFound,
            placeholder: 'Scan product barcode to add to invoice...',
            autoFocus: true,
            showProductPreview: false
          })
        )
      )
    ),

    // Main Content
    React.createElement('div', { className: 'flex gap-6 p-6' },
      // Left - Invoice Form
      React.createElement('div', { className: 'flex-1 space-y-6' },
        // Customer & Invoice Details Card
        React.createElement(Card, null,
          React.createElement(CardHeader, { className: 'pb-4' },
            React.createElement(CardTitle, { className: 'text-base' }, 'Invoice Details')
          ),
          React.createElement(CardContent, { className: 'space-y-4' },
            // Row 1: Customer and Date
            React.createElement('div', { className: 'grid grid-cols-2 gap-4' },
              React.createElement('div', null,
                React.createElement(Label, { className: 'text-sm font-medium mb-2 block' }, 'Customer *'),
                renderCustomerPicker()
              ),
              React.createElement('div', null,
                React.createElement(Label, { className: 'text-sm font-medium mb-2 block' }, 'Invoice Date *'),
                React.createElement('input', {
                  type: 'date',
                  value: invoiceDate,
                  onChange: function(e) { setInvoiceDate(e.target.value); },
                  className: 'w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  'data-testid': 'invoice-date'
                })
              )
            ),
            // Customer Info Display
            selectedCustomer && React.createElement('div', { className: 'bg-blue-50 border border-blue-100 rounded-lg p-3' },
              React.createElement('div', { className: 'flex flex-wrap gap-4 text-sm' },
                React.createElement('span', { className: 'font-semibold text-blue-900' }, selectedCustomer.name),
                selectedCustomer.gstin && React.createElement('span', { className: 'text-blue-700' }, 'GSTIN: ' + selectedCustomer.gstin),
                React.createElement('span', { className: 'text-blue-600' }, selectedCustomer.phone),
                React.createElement('span', { className: 'text-blue-600' }, selectedCustomer.address)
              )
            ),
            // Row 2: Payment, Due Date, IGST
            React.createElement('div', { className: 'grid grid-cols-3 gap-4' },
              React.createElement('div', null,
                React.createElement(Label, { className: 'text-sm font-medium mb-2 block' }, 'Payment Mode'),
                React.createElement('select', {
                  value: paymentMode,
                  onChange: function(e) { setPaymentMode(e.target.value); },
                  className: 'w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                },
                  React.createElement('option', { value: 'cash' }, 'Cash'),
                  React.createElement('option', { value: 'upi' }, 'UPI'),
                  React.createElement('option', { value: 'credit' }, 'Credit'),
                  React.createElement('option', { value: 'bank_transfer' }, 'Bank Transfer'),
                  React.createElement('option', { value: 'cheque' }, 'Cheque')
                )
              ),
              React.createElement('div', null,
                React.createElement(Label, { className: 'text-sm font-medium mb-2 block' }, 'Due Date'),
                React.createElement('input', {
                  type: 'date',
                  value: dueDate,
                  onChange: function(e) { setDueDate(e.target.value); },
                  className: 'w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  'data-testid': 'due-date'
                })
              ),
              React.createElement('div', { className: 'flex items-end pb-2' },
                React.createElement('label', { className: 'flex items-center gap-2 cursor-pointer' },
                  React.createElement('input', {
                    type: 'checkbox',
                    checked: isIGST,
                    onChange: function(e) { setIsIGST(e.target.checked); },
                    className: 'w-5 h-5 rounded border-gray-300'
                  }),
                  React.createElement('span', { className: 'text-sm font-medium' }, 'Inter-State (IGST)')
                )
              )
            )
          )
        ),

        // Invoice Items Card
        React.createElement(Card, null,
          React.createElement(CardHeader, { className: 'pb-4' },
            React.createElement('div', { className: 'flex items-center justify-between' },
              React.createElement(CardTitle, { className: 'text-base' }, 'Invoice Items'),
              React.createElement('div', { className: 'flex items-center gap-4' },
                // Search Mode Toggle
                React.createElement('div', { className: 'flex items-center gap-2 bg-gray-100 rounded-lg p-1' },
                  React.createElement('button', {
                    onClick: function() { setSearchMode('search'); },
                    className: 'flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ' + 
                      (searchMode === 'search' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-800'),
                    'data-testid': 'search-mode-btn'
                  },
                    React.createElement(Search, { className: 'w-3 h-3' }),
                    'Search'
                  ),
                  React.createElement('button', {
                    onClick: function() { setSearchMode('dropdown'); },
                    className: 'flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ' +
                      (searchMode === 'dropdown' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-800'),
                    'data-testid': 'dropdown-mode-btn'
                  },
                    React.createElement(Package, { className: 'w-3 h-3' }),
                    'Dropdown'
                  )
                ),
                React.createElement(Button, { onClick: addItem, size: 'sm' },
                  React.createElement(Plus, { className: 'w-4 h-4 mr-1' }),
                  'Add Item'
                )
              )
            ),
            searchMode === 'search' && React.createElement('p', { className: 'text-xs text-gray-500 mt-2' },
              '💡 Type at least 3 characters to search by Product Name, SKU, or Barcode'
            )
          ),
          React.createElement(CardContent, null,
            // Always-visible Fast Scan bar — auto-focused on mount, never
            // scrolls the page. Newest scan lands at row 0; duplicate
            // scans bump qty AND move the existing row to row 0.
            React.createElement('div', { className: 'mb-3 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg' },
              React.createElement('form', { onSubmit: handleFastScanSubmit, className: 'flex items-center gap-2' },
                React.createElement('div', { className: 'flex items-center gap-2 px-2 py-1 bg-white rounded border border-emerald-200' },
                  React.createElement(ScanBarcode, { className: 'w-4 h-4 text-emerald-600' }),
                  React.createElement('span', { className: 'text-xs font-semibold text-emerald-700 uppercase tracking-wide' }, 'Fast Scan')
                ),
                React.createElement('input', {
                  ref: fastScanRef,
                  type: 'text',
                  value: fastScanValue,
                  onChange: function(e) { setFastScanValue(e.target.value); },
                  placeholder: fastScanBusy ? 'Looking up...' : 'Scan or type a barcode and press Enter — newest scan stays on top',
                  disabled: fastScanBusy,
                  autoFocus: true,
                  className: 'flex-1 h-10 px-3 border border-emerald-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500',
                  'data-testid': 'fast-scan-input'
                }),
                fastScanValue && React.createElement('button', {
                  type: 'button',
                  onClick: function() {
                    setFastScanValue('');
                    if (fastScanRef.current) {
                      try { fastScanRef.current.focus({ preventScroll: true }); } catch (_) { fastScanRef.current.focus(); }
                    }
                  },
                  className: 'p-1 hover:bg-emerald-100 rounded',
                  'data-testid': 'fast-scan-clear'
                },
                  React.createElement(X, { className: 'w-4 h-4 text-emerald-700' })
                )
              )
            ),
            // Scroll container with FROZEN header (Oct 2026)
            React.createElement('div', { className: 'max-h-[65vh] overflow-y-auto rounded-lg', 'data-testid': 'invoice-items-scroll' },
              // Table Header — sticky so headings stay visible while scrolling
              React.createElement('div', { className: 'sticky top-0 z-20 grid grid-cols-12 gap-2 py-2 px-3 bg-slate-100 rounded-t-lg text-xs font-semibold text-slate-600 uppercase tracking-wide shadow-sm' },
                React.createElement('div', { className: 'col-span-2' }, 'Product'),
                React.createElement('div', { className: 'col-span-1 text-right' }, 'MRP (₹)'),
                React.createElement('div', { className: 'col-span-1 text-center' }, 'Qty'),
                React.createElement('div', { className: 'col-span-1 text-center' }, 'Unit'),
                React.createElement('div', { className: 'col-span-2 text-center' }, 'Sell Price (₹)'),
                React.createElement('div', { className: 'col-span-1 text-center' }, 'Disc%'),
                React.createElement('div', { className: 'col-span-1 text-center' }, 'GST%'),
                React.createElement('div', { className: 'col-span-2 text-right' }, 'Amount'),
                React.createElement('div', { className: 'col-span-1' }, '')
              ),
              // Items
              items.length === 0 ?
              React.createElement('div', { className: 'text-center py-12 text-slate-400 border border-t-0 rounded-b-lg' },
                React.createElement('p', { className: 'mb-2' }, 'No items added yet'),
                React.createElement('p', { className: 'text-xs' }, 'Click "Add Item" or scan a barcode to start')
              ) :
              React.createElement('div', { className: 'border border-t-0 rounded-b-lg divide-y' },
                items.map(function(item, idx) {
                  var calc = calculateItemTotal(item);
                  var itemSearchQuery = searchQueries[idx] || '';
                  var itemSearchResults = searchResults[idx] || [];
                  var isSearching = searchLoading[idx] || false;
                  var showResultsDropdown = showDropdowns[idx] || false;
                  
                  return React.createElement('div', { 
                    key: idx, 
                    className: 'relative grid grid-cols-12 gap-2 items-center py-3 px-3 hover:bg-slate-50' + (idx === 0 && items.length > 1 ? ' bg-emerald-50/40' : ''),
                    'data-testid': 'invoice-item-' + idx
                  },
                    // "Newest" pill on row 0 when there are multiple rows
                    idx === 0 && items.length > 1 && React.createElement('div', {
                      className: 'absolute -mt-7 ml-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 border border-emerald-300 rounded-full',
                      'data-testid': 'newest-row-pill'
                    }, '↑ Newest'),
                    // Product - Search Mode vs Dropdown Mode
                    React.createElement('div', { className: 'col-span-2 relative' },
                      searchMode === 'search' ? (
                        // Search Mode - Typeahead
                        item.product_id ? (
                          // Product selected - show selected product + optional batch picker
                          React.createElement('div', { className: 'space-y-2' },
                            React.createElement('div', { className: 'flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg' },
                              React.createElement('div', { className: 'flex-1 min-w-0' },
                                React.createElement('p', { className: 'font-medium text-sm text-blue-900 truncate' }, item.product_name),
                                React.createElement('p', { className: 'text-xs text-blue-600' }, 'Stock: ', item.available_stock, ' ', item.unit, item.batch_number ? ' · ' + item.batch_number : '')
                              ),
                              React.createElement('button', {
                                onClick: function() { clearProductSelection(idx); },
                                className: 'p-1 hover:bg-blue-100 rounded',
                                'data-testid': 'clear-product-' + idx
                              },
                                React.createElement(X, { className: 'w-4 h-4 text-blue-600' })
                              )
                            ),
                            // Batch picker / loose-stock badge (shared with Dropdown mode)
                            renderBatchPicker(item, idx)
                          )
                        ) : (
                          // No product - show search input
                          React.createElement('div', { className: 'relative', ref: function(el) { searchRefs.current[idx] = el; } },
                            React.createElement('div', { className: 'relative' },
                              React.createElement(Search, { className: 'absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400' }),
                              React.createElement('input', {
                                type: 'text',
                                value: itemSearchQuery,
                                onChange: function(e) { searchProducts(e.target.value, idx); },
                                onFocus: function() { 
                                  if (itemSearchQuery.length >= 3) {
                                    var newShow = Object.assign({}, showDropdowns);
                                    newShow[idx] = true;
                                    setShowDropdowns(newShow);
                                  }
                                },
                                placeholder: 'Search product...',
                                className: 'w-full h-9 pl-9 pr-3 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                                'data-testid': 'product-search-' + idx
                              }),
                              isSearching && React.createElement(Loader2, { className: 'absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-500 animate-spin' })
                            ),
                            // Search Results Dropdown
                            showResultsDropdown && itemSearchResults.length > 0 && React.createElement('div', { 
                              className: 'absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto'
                            },
                              itemSearchResults.map(function(product) {
                                return React.createElement('div', {
                                  key: product.id,
                                  onClick: function() { selectSearchProduct(idx, product); },
                                  className: 'px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 ' + (product.is_out_of_stock ? 'opacity-50' : '')
                                },
                                  React.createElement('div', { className: 'flex items-center justify-between' },
                                    React.createElement('div', { className: 'flex-1 min-w-0' },
                                      React.createElement('p', { className: 'font-medium text-sm truncate' }, product.name),
                                      React.createElement('p', { className: 'text-xs text-gray-500' }, 
                                        product.product_id, product.barcode ? ' | ' + product.barcode : ''
                                      )
                                    ),
                                    React.createElement('div', { className: 'text-right ml-2' },
                                      React.createElement('p', { className: 'font-semibold text-sm text-green-600' }, '₹', product.outward_selling_price || product.selling_price || product.mrp || 0),
                                      product.is_out_of_stock ? (
                                        React.createElement(Badge, { className: 'bg-red-100 text-red-700 text-xs' }, 'Out of Stock')
                                      ) : (
                                        React.createElement('p', { className: 'text-xs text-gray-500' }, 'Stock: ', product.current_stock, ' ', product.unit)
                                      )
                                    )
                                  )
                                );
                              })
                            ),
                            // No results message
                            showResultsDropdown && itemSearchQuery.length >= 3 && itemSearchResults.length === 0 && !isSearching && 
                              React.createElement('div', { className: 'absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500 text-sm' },
                                'No products found for "', itemSearchQuery, '"'
                              )
                          )
                        )
                      ) : (
                        // Dropdown Mode - Traditional Select + batch picker
                        React.createElement('div', { className: 'space-y-2' },
                          React.createElement('select', {
                            value: item.product_id,
                            onChange: function(e) { updateItem(idx, 'product_id', e.target.value); },
                            className: 'w-full h-9 px-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500',
                            'data-testid': 'product-select-' + idx
                          },
                            React.createElement('option', { value: '' }, '-- Select Product --'),
                            products.slice().sort(function(a, b) {
                              return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
                            }).map(function(p) {
                              return React.createElement('option', { key: p.id, value: p.id }, 
                                p.name + ' (Stock: ' + (p.current_stock || 0) + ')'
                              );
                            })
                          ),
                          renderBatchPicker(item, idx)
                        )
                      ),
                      item.quantity > item.available_stock && item.product_id && React.createElement('p', { className: 'text-xs text-red-500 mt-1' },
                        '⚠ Low stock! Available: ' + item.available_stock
                      )
                    ),
                    // MRP (read-only, sourced from Product Master / payload)
                    React.createElement('div', { className: 'col-span-1 text-right' },
                      React.createElement('div', { 
                        className: 'w-full h-10 px-2 flex items-center justify-end text-sm font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-md',
                        'data-testid': 'invoice-item-mrp-' + idx
                      }, '₹', Number(item.mrp || 0).toFixed(2))
                    ),
                    // Qty - allows up to 2 decimal places (essential for KG / GRAMS / LITRE)
                    React.createElement('div', { className: 'col-span-1' },
                      React.createElement('input', {
                        type: 'text',
                        inputMode: 'decimal',
                        value: item.quantity === 0 ? '' : item.quantity,
                        onChange: function(e) {
                          var val = e.target.value;
                          // Permit empty, integers, and partial/full decimals up to 2 dp (e.g. "1", "1.", "1.8", "1.89").
                          // We deliberately store the raw string mid-typing so the trailing "." is preserved on re-render.
                          if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                            updateItem(idx, 'quantity', val === '' ? 0 : val);
                          }
                        },
                        onBlur: function(e) {
                          // On blur, normalise to a clean number so calculations downstream are deterministic.
                          var n = parseFloat(e.target.value);
                          updateItem(idx, 'quantity', isNaN(n) ? 0 : n);
                        },
                        className: 'w-full h-10 px-3 border border-gray-300 rounded-md text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                        placeholder: '0',
                        'data-testid': 'qty-input-' + idx
                      })
                    ),
                    // Unit
                    React.createElement('div', { className: 'col-span-1' },
                      React.createElement('select', {
                        value: String(item.unit || 'PCS').toUpperCase() === 'KG' ? 'KGS' : (item.unit || 'PCS'),
                        onChange: function(e) { updateItem(idx, 'unit', e.target.value); },
                        className: 'w-full h-10 px-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-blue-500',
                        'data-testid': 'unit-select-' + idx
                      },
                        React.createElement('option', { value: 'PCS' }, 'PCS'),
                        React.createElement('option', { value: 'KGS' }, 'KG'),
                        React.createElement('option', { value: 'GRAMS' }, 'GM'),
                        React.createElement('option', { value: 'LITRE' }, 'LTR'),
                        React.createElement('option', { value: 'ML' }, 'ML'),
                        React.createElement('option', { value: 'BOX' }, 'BOX'),
                        React.createElement('option', { value: 'PACK' }, 'PCK'),
                        React.createElement('option', { value: 'SET' }, 'SET'),
                        React.createElement('option', { value: 'DOZEN' }, 'DZN')
                      )
                    ),
                    // Rate
                    React.createElement('div', { className: 'col-span-2 relative' },
                      React.createElement('input', {
                        type: 'text',
                        inputMode: 'decimal',
                        value: item.unit_price,
                        onChange: function(e) { 
                          var val = e.target.value;
                          if (val === '' || !isNaN(parseFloat(val))) {
                            updateItem(idx, 'unit_price', val === '' ? 0 : parseFloat(val)); 
                          }
                        },
                        className: 'w-full h-10 px-3 ' + (item.outward_auto_applied ? 'pr-12 ' : '') + 'border ' + (item.slab_disagreement ? 'border-amber-400 ring-1 ring-amber-300' : 'border-gray-300') + ' rounded-md text-sm text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ' + (item.outward_auto_applied ? 'bg-emerald-50/40' : ''),
                        placeholder: '0.00',
                        'data-testid': 'invoice-rate-input-' + idx,
                      }),
                      item.outward_auto_applied && React.createElement('span', {
                        className: 'absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-[1px] text-[9px] font-semibold text-emerald-700 ring-1 ring-emerald-200',
                        title: 'Auto-filled from Margin Master · Our Margin = ' + item.outward_auto_margin_pct + '%',
                        'data-testid': 'invoice-rate-auto-badge-' + idx,
                      }, 'Auto'),
                    ),
                    // Discount
                    React.createElement('div', { className: 'col-span-1' },
                      React.createElement('input', {
                        type: 'text',
                        inputMode: 'decimal',
                        value: item.discount_percent || 0,
                        onChange: function(e) { 
                          var val = e.target.value;
                          if (val === '' || !isNaN(parseFloat(val))) {
                            updateItem(idx, 'discount_percent', val === '' ? 0 : parseFloat(val)); 
                          }
                        },
                        className: 'w-full h-10 px-3 border border-gray-300 rounded-md text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                        placeholder: '0'
                      })
                    ),
                    // GST
                    React.createElement('div', { className: 'col-span-1' },
                      React.createElement('input', {
                        type: 'text',
                        inputMode: 'decimal',
                        value: item.gst_percentage,
                        onChange: function(e) { 
                          var val = e.target.value;
                          if (val === '' || !isNaN(parseFloat(val))) {
                            updateItem(idx, 'gst_percentage', val === '' ? 0 : parseFloat(val)); 
                          }
                        },
                        className: 'w-full h-10 px-3 border border-gray-300 rounded-md text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                        placeholder: '0'
                      })
                    ),
                    // Amount
                    React.createElement('div', { className: 'col-span-2 text-right font-semibold text-green-700 text-base' },
                      formatCurrency(calc.total)
                    ),
                    // Delete
                    React.createElement('div', { className: 'col-span-1 text-center' },
                      React.createElement(Button, { 
                        variant: 'ghost', 
                        size: 'sm',
                        onClick: function() { removeItem(idx); },
                        className: 'h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50'
                      },
                        React.createElement(Trash2, { className: 'w-4 h-4' })
                      )
                    )
                  );
                })
              )
            )
          )
        ),

        // Notes Card
        React.createElement(Card, null,
          React.createElement(CardContent, { className: 'pt-4' },
            React.createElement(Label, { className: 'text-sm font-medium mb-2 block' }, 'Notes / Terms'),
            React.createElement(Textarea, {
              value: notes,
              onChange: function(e) { setNotes(e.target.value); },
              placeholder: 'Add any notes or payment terms...',
              rows: 2,
              className: 'text-sm'
            })
          )
        )
      ),

      // Right - Summary Panel
      React.createElement('div', { className: 'w-80' },
        React.createElement(Card, { className: 'sticky top-20' },
          React.createElement(CardHeader, { className: 'bg-slate-50 rounded-t-lg' },
            React.createElement(CardTitle, { className: 'flex items-center gap-2 text-base' },
              React.createElement(Calculator, { className: 'w-5 h-5' }),
              'Bill Summary'
            )
          ),
          React.createElement(CardContent, { className: 'space-y-3 pt-4' },
            // Item count
            React.createElement('div', { className: 'flex justify-between text-sm text-slate-600' },
              React.createElement('span', null, 'Items'),
              React.createElement('span', null, items.length + ' item(s)')
            ),
            // Subtotal
            React.createElement('div', { className: 'flex justify-between text-sm' },
              React.createElement('span', null, 'Subtotal'),
              React.createElement('span', { className: 'font-medium' }, formatCurrency(totals.subtotal))
            ),
            // Discount Input
            React.createElement('div', { className: 'flex justify-between items-center' },
              React.createElement('span', { className: 'text-sm' }, 'Discount'),
              React.createElement('div', { className: 'flex items-center gap-2' },
                React.createElement('input', {
                  type: 'number',
                  min: '0',
                  max: '100',
                  value: discountPercent,
                  onChange: function(e) { setDiscountPercent(parseFloat(e.target.value) || 0); },
                  className: 'w-16 h-8 px-2 border border-gray-300 rounded text-sm text-center'
                }),
                React.createElement('span', { className: 'text-sm' }, '%'),
                totals.invoiceDiscount > 0 && React.createElement('span', { className: 'text-red-600 text-sm' }, '-' + formatCurrency(totals.invoiceDiscount))
              )
            ),
            // Divider
            React.createElement('div', { className: 'border-t pt-3' }),
            // Taxable
            React.createElement('div', { className: 'flex justify-between text-sm' },
              React.createElement('span', null, 'Taxable Amount'),
              React.createElement('span', null, formatCurrency(totals.subtotalAfterDiscount))
            ),
            // GST breakdown
            !isIGST && React.createElement('div', { className: 'space-y-2' },
              React.createElement('div', { className: 'flex justify-between text-sm text-slate-600' },
                React.createElement('span', null, 'CGST'),
                React.createElement('span', null, formatCurrency(totals.cgst))
              ),
              React.createElement('div', { className: 'flex justify-between text-sm text-slate-600' },
                React.createElement('span', null, 'SGST'),
                React.createElement('span', null, formatCurrency(totals.sgst))
              )
            ),
            isIGST && React.createElement('div', { className: 'flex justify-between text-sm text-slate-600' },
              React.createElement('span', null, 'IGST'),
              React.createElement('span', null, formatCurrency(totals.igst))
            ),
            // Round Off
            React.createElement('div', { className: 'flex justify-between items-center' },
              React.createElement('span', { className: 'text-sm' }, 'Round Off'),
              React.createElement('input', {
                type: 'number',
                step: '0.01',
                value: roundOff,
                onChange: function(e) { setRoundOff(e.target.value); },
                className: 'w-20 h-8 px-2 border border-gray-300 rounded text-sm text-center'
              })
            ),
            // Grand Total
            React.createElement('div', { className: 'border-t pt-4 mt-4' },
              React.createElement('div', { className: 'flex justify-between items-center' },
                React.createElement('span', { className: 'text-lg font-bold' }, 'Grand Total'),
                React.createElement('span', { className: 'text-2xl font-bold text-green-600' }, formatCurrency(totals.grandTotal))
              )
            ),
            // Action Buttons
            React.createElement('div', { className: 'space-y-3 pt-4' },
              React.createElement(Button, {
                className: 'w-full h-12 text-lg gap-2 bg-green-600 hover:bg-green-700',
                onClick: function() { createInvoice(true); },
                disabled: submitting || items.length === 0,
                'data-testid': 'invoice-save-finalize-btn'
              },
                submitting ? React.createElement(Loader2, { className: 'w-5 h-5 animate-spin' }) : React.createElement(CheckCircle2, { className: 'w-5 h-5' }),
                'Save & Finalize'
              ),
              React.createElement(Button, {
                className: 'w-full gap-2',
                onClick: function() { createInvoice(false); },
                disabled: submitting || items.length === 0,
                variant: 'outline',
                'data-testid': 'invoice-save-draft-btn'
              },
                React.createElement(Save, { className: 'w-4 h-4' }),
                'Save as Draft'
              ),
              React.createElement('p', { className: 'text-xs text-slate-500 text-center' },
                'Finalizing will deduct stock from inventory'
              )
            )
          )
        )
      )
    )
  );
}

export default InvoiceCreate;
